// F-U / F-W: the view state the Exhibits and Zones lists share.
//
// `useListUrlState` — Status, search and filters live in the URL, so Back
// from a form (and a shared link) restores the exact view.
// `useListRows` — given the fetched rows: cascade-pruned filters + options
// (with a "removed from filter" toast), the filtered rows, remembering the
// view + scroll for the forms' Back, and batch selection (cleared whenever
// Status / search / filters change, so an action never touches rows the
// user can't see).

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LIST_STATUS_OPTIONS, type ListStatus } from "@shared/api/listStatus";
import {
  EMPTY_FILTERS,
  activeFilterCount,
  applyFilters,
  filtersFromParams,
  filtersToParams,
  reconcileFilters,
  type ExhibitFilters,
  type FilterKey,
  type FilterOptions,
  type FilterableRow,
} from "./listFilters";
import {
  rememberListUrl,
  saveListScroll,
  takeListScroll,
  type ListKind,
} from "./listReturn";

const FILTER_NOUN: Record<FilterKey, [string, string]> = {
  years: ["year", "years"],
  exhibitions: ["exhibition", "exhibitions"],
  zones: ["zone", "zones"],
  departments: ["department", "departments"],
};

const isListStatus = (v: string | null): v is ListStatus =>
  LIST_STATUS_OPTIONS.some((o) => o.value === v);

export interface ListUrlState {
  status: ListStatus;
  setStatus: (next: ListStatus) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  requestedFilters: ExhibitFilters;
  setFilters: (next: ExhibitFilters) => void;
  clearAll: () => void;
  /** Changes whenever Status / search / filters change. */
  viewKey: string;
}

export function useListUrlState(): ListUrlState {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const requestedFilters = useMemo(
    () => filtersFromParams(searchParams),
    [searchParams],
  );
  const updateParams = (mutate: (p: URLSearchParams) => URLSearchParams) =>
    setSearchParams((prev) => mutate(new URLSearchParams(prev)), {
      replace: true,
    });
  return {
    status: isListStatus(statusParam) ? statusParam : "active",
    setStatus: (next) =>
      updateParams((p) => {
        if (next === "active") p.delete("status");
        else p.set("status", next);
        return p;
      }),
    searchQuery: searchParams.get("q") ?? "",
    setSearchQuery: (q) =>
      updateParams((p) => {
        if (q) p.set("q", q);
        else p.delete("q");
        return p;
      }),
    requestedFilters,
    setFilters: (next) => updateParams((p) => filtersToParams(next, p)),
    clearAll: () =>
      updateParams((p) => {
        p.delete("q");
        return filtersToParams(EMPTY_FILTERS, p);
      }),
    viewKey: searchParams.toString(),
  };
}

export interface ListRows<T> {
  filters: ExhibitFilters;
  options: FilterOptions;
  filtered: T[];
  hasNarrowing: boolean;
  /** Navigate to a form, remembering the list scroll for Back. */
  openPage: (path: string) => void;
  selected: Set<string>;
  setSelected: (next: Set<string>) => void;
  selectableRows: T[];
  selectedRows: T[];
  allSelected: boolean;
  toggleOne: (id: string, on: boolean) => void;
  toggleAll: (on: boolean) => void;
}

export function useListRows<T extends FilterableRow & { state: string }>(
  kind: ListKind,
  url: ListUrlState,
  rows: T[],
  loading: boolean,
): ListRows<T> {
  const navigate = useNavigate();
  const location = useLocation();

  // Cascade: drop selections the narrowed options no longer offer.
  const { filters, options, removed } = useMemo(
    () => reconcileFilters(rows, url.requestedFilters),
    [rows, url.requestedFilters],
  );
  const removedKey = JSON.stringify(removed);
  useEffect(() => {
    const entries = Object.entries(removed) as [FilterKey, number][];
    if (!entries.length) return;
    url.setFilters(filters);
    toast(
      entries
        .map(
          ([key, n]) =>
            `${n} ${FILTER_NOUN[key][n === 1 ? 0 : 1]} removed from filter`,
        )
        .join(" · "),
    );
  }, [removedKey]);

  const filtered = useMemo(
    () => applyFilters(rows, url.searchQuery, filters),
    [rows, url.searchQuery, filters],
  );

  // Remember this view for the forms' Back button.
  useEffect(() => {
    rememberListUrl(kind, `${location.pathname}${location.search}`);
  }, [kind, location.pathname, location.search]);

  // Restore the scroll offset saved when a row was opened, once rows render.
  const [savedScroll] = useState(() => takeListScroll(kind));
  const pendingScroll = useRef<number | null>(savedScroll);
  useEffect(() => {
    if (loading || pendingScroll.current === null) return;
    window.scrollTo(0, pendingScroll.current);
    pendingScroll.current = null;
  }, [loading]);

  const openPage = (path: string) => {
    saveListScroll(kind, window.scrollY);
    navigate(path);
  };

  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelected(new Set());
  }, [url.viewKey]);

  const selectableRows = filtered.filter(
    (r) => r.state !== "archived" && r.state !== "purged",
  );
  const selectedRows = selectableRows.filter((r) => selected.has(r.id));

  return {
    filters,
    options,
    filtered,
    hasNarrowing:
      url.searchQuery.trim() !== "" || activeFilterCount(filters) > 0,
    openPage,
    selected,
    setSelected,
    selectableRows,
    selectedRows,
    allSelected:
      selectableRows.length > 0 &&
      selectedRows.length === selectableRows.length,
    toggleOne: (id, on) =>
      setSelected((prev) => {
        const next = new Set(prev);
        if (on) next.add(id);
        else next.delete(id);
        return next;
      }),
    toggleAll: (on) =>
      setSelected(on ? new Set(selectableRows.map((r) => r.id)) : new Set()),
  };
}
